<template>
  <div>
    <!-- Button to open icon selection dialog -->
    <v-btn
      :color="buttonColor || 'primary'"
      :variant="buttonVariant || 'outlined'"
      :density="buttonDensity || 'default'"
      :rounded="buttonRounded || '0'"
      :disabled="disabled"
      size="large"
      @click="openIconDialog"
      class="icon-selection-btn"
      :class="{ 'error--text': errorMessages.length > 0 }"
    >
      <v-icon v-if="modelValue" class="mr-2"> mdi-{{ modelValue }} </v-icon>
      <span>{{ buttonText || "Select Icon" }}</span>
      <v-icon class="ml-2">mdi-chevron-down</v-icon>
    </v-btn>

    <!-- Error messages -->
    <div v-if="errorMessages.length > 0" class="error-messages mt-1">
      <div
        v-for="(error, index) in errorMessages"
        :key="index"
        class="text-caption error--text"
      >
        {{ error }}
      </div>
    </div>

    <!-- Icon Selection Dialog -->
    <v-dialog v-model="dialog" max-width="800px" persistent>
      <v-card style="height: 600px; display: flex; flex-direction: column">
        <v-card-title
          :style="{
            backgroundColor: $store.state.config.siteConfig.toolbar_colour,
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }"
          class="text-white pa-3"
        >
          <span class="text-h6">Select Icon</span>
        </v-card-title>

        <v-card-text class="pa-4" style="flex: 1; overflow-y: auto">
          <!-- Search Field -->
          <v-text-field
            v-model="searchQuery"
            label="Search icons..."
            variant="outlined"
            density="compact"
            rounded="0"
            prepend-inner-icon="mdi-magnify"
            clearable
            class="mb-4"
            style="max-width: 33%"
            @update:modelValue="filterIcons"
          />

          <!-- Icons Grid -->
          <div
            style="
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
              gap: 8px;
              height: 350px;
              overflow-y: auto;
              padding-top: 5px;
              align-content: start;
            "
          >
            <div
              v-for="icon in filteredIcons"
              :key="icon.value"
              @click="selectIcon(icon.value)"
              style="
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 12px 8px;
                border: 2px solid transparent;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.2s ease;
                background-color: #fafafa;
                height: 85px;
                min-height: 85px;
                max-height: 85px;
              "
              :style="
                selectedIcon === icon.value
                  ? 'border-color: #1976d2; background-color: #e3f2fd; box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);'
                  : hoveredIcon === icon.value
                    ? 'border-color: #e0e0e0; background-color: #f0f0f0; transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);'
                    : ''
              "
              @mouseenter="hoveredIcon = icon.value"
              @mouseleave="hoveredIcon = null"
            >
              <v-icon
                :color="selectedIcon === icon.value ? 'primary' : 'grey'"
                size="24"
                class="mb-2"
              >
                mdi-{{ icon.value }}
              </v-icon>
              <div
                style="
                  font-size: 0.7rem;
                  color: #666;
                  text-align: center;
                  word-break: break-word;
                  line-height: 1.2;
                  height: 32px;
                  overflow: hidden;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                {{ icon.text }}
              </div>
            </div>
          </div>

          <!-- No results message -->
          <div v-if="filteredIcons.length === 0" class="text-center pa-8">
            <v-icon size="48" color="grey">mdi-magnify</v-icon>
            <div class="text-h6 mt-4 text-grey">No icons found</div>
            <div class="text-body-2 text-grey">Try a different search term</div>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn color="error" variant="text" rounded="0" @click="closeDialog">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="flat"
            rounded="0"
            @click="confirmSelection"
            :disabled="!selectedIcon"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "IconSelection",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    icons: {
      type: Array,
      default: () => [],
    },
    buttonText: {
      type: String,
      default: "Select Icon",
    },
    buttonColor: {
      type: String,
      default: "primary",
    },
    buttonVariant: {
      type: String,
      default: "outlined",
    },
    buttonDensity: {
      type: String,
      default: "default",
    },
    buttonRounded: {
      type: String,
      default: "0",
    },
    dialogMaxWidth: {
      type: String,
      default: "90vw",
    },
    dialogMaxHeight: {
      type: String,
      default: "90vh",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      dialog: false,
      searchQuery: "",
      filteredIcons: [],
      selectedIcon: "",
      hoveredIcon: null,
    };
  },
  watch: {
    icons: {
      immediate: true,
      handler() {
        this.filteredIcons = [...this.icons];
      },
    },
    modelValue: {
      immediate: true,
      handler() {
        this.selectedIcon = this.modelValue;
      },
    },
  },
  methods: {
    openIconDialog() {
      this.dialog = true;
      this.searchQuery = "";
      this.filteredIcons = [...this.icons];
    },
    closeDialog() {
      this.dialog = false;
      this.searchQuery = "";
      this.selectedIcon = this.modelValue;
    },
    filterIcons() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        this.filteredIcons = [...this.icons];
      } else {
        const query = this.searchQuery.toLowerCase();
        this.filteredIcons = this.icons.filter(
          (icon) =>
            icon.text.toLowerCase().includes(query) ||
            icon.value.toLowerCase().includes(query),
        );
      }
    },
    selectIcon(iconValue) {
      this.selectedIcon = iconValue;
    },
    confirmSelection() {
      this.$emit("update:modelValue", this.selectedIcon);
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.icon-selection-btn {
  min-width: 140px;
}
</style>
